/**
 * Step 5 — Time slot picker (+ contact details for anonymous users).
 *
 * The backend returns a flat array of "YYYY-MM-DD HH:mm:ss" strings. We
 * group them by date so the user sees a clear "Today / Tomorrow / Mon Jun 13"
 * style layout with clickable time chips per day.
 *
 * If the user is logged in we already have their email; for anonymous users
 * we collect email + optional name + phone on this step. The contact fields
 * are forwarded to BookingPage but currently dropped before the create-ticket
 * request — the backend doesn't accept `client_name` / `phone` / `email`
 * yet, so we hold onto them locally until that lands.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchTimeSuggestions,
  getServiceCategoryId,
  getServicePrice,
  getServiceUserId,
  pickTranslation,
  resolveStorageUrl,
} from "../../helper/bookingApi";
import {
  BOOKING_FONT_FAMILY,
  EmptyState,
  RetryButton,
  StepCard,
  STEP_PAD,
} from "./BookingPrimitives";
import {
  dayKey,
  formatClockTime,
  formatDayLabel,
  formatMonth,
  formatWeekday,
  parseSlotDate,
} from "./bookingFormat";

const WORKER_FALLBACK_AVATAR = "/assets/images/avatars/user.png";

const CALENDAR_MONTHS = {
  hy: [
    "Հունվար",
    "Փետրվար",
    "Մարտ",
    "Ապրիլ",
    "Մայիս",
    "Հունիս",
    "Հուլիս",
    "Օգոստոս",
    "Սեպտեմբեր",
    "Հոկտեմբեր",
    "Նոյեմբեր",
    "Դեկտեմբեր",
  ],
  en: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
  ru: [
    "Январь",
    "Февраль",
    "Март",
    "Апрель",
    "Май",
    "Июнь",
    "Июль",
    "Август",
    "Сентябрь",
    "Октябрь",
    "Ноябрь",
    "Декабрь",
  ],
};

function emailIsValid(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function phoneIsValid(value) {
  // Accept anything that contains 6+ digits (allowing +, spaces, parens,
  // dashes). The backend will normalize, so we keep the UI permissive.
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length >= 6;
}

function getPrimaryService(value) {
  if (Array.isArray(value)) return value.filter(Boolean)[0] || null;
  return value || null;
}

function getSelectedServices(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
}

function formatServicePrice(value) {
  return `${Number(value || 0).toLocaleString("hy-AM")} ֏`;
}

function getDurationMinutes(item) {
  const raw =
    item?.average_waiting ??
    item?.duration ??
    item?.waiting_time ??
    item?.category?.average_waiting;
  const minutes = Number(raw);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
}

function formatDurationMinutes(minutes, lang) {
  if (!Number.isFinite(minutes) || minutes <= 0) return "";
  if (minutes < 60) return lang === "en" ? `${minutes} min` : `${minutes} ր`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!rest) return lang === "en" ? `${hours} h` : `${hours} ժ`;
  return lang === "en" ? `${hours} h ${rest} min` : `${hours} ժ ${rest} ր`;
}

function getWorkerName(worker) {
  const fullName = [worker?.name, worker?.surname].filter(Boolean).join(" ").trim();
  return (
    fullName ||
    worker?.full_name ||
    worker?.fullName ||
    worker?.user?.name ||
    ""
  );
}

function getDisplayText(value, lang) {
  if (!value) return "";
  if (Array.isArray(value)) {
    return value.map((item) => getDisplayText(item, lang)).filter(Boolean).join(", ");
  }
  if (typeof value === "string") return value;
  return (
    pickTranslation(value, lang) ||
    value?.name ||
    value?.title ||
    value?.label ||
    value?.translate?.name ||
    value?.translate?.title ||
    ""
  );
}

function getWorkerRole(worker, lang, service, selectedWorkerProfession) {
  const directCandidates = [
    selectedWorkerProfession,
    worker?.professions?.[0]?.name,
    worker?.profession,
    worker?.proffesion,
  ];

  for (const candidate of directCandidates) {
    const text = getDisplayText(candidate, lang);
    if (text) return text;
  }

  const selectedServices = getSelectedServices(service);
  for (const item of selectedServices) {
    const text = getDisplayText(item?.profession || item?.category || item, lang);
    if (text) return text;
  }

  return "";
}

function getWorkerAvatar(worker) {
  return resolveStorageUrl(
    worker?.avatar ||
      worker?.avatar_path ||
      worker?.image ||
      worker?.image_path ||
      worker?.photo ||
      worker?.photo_path ||
      worker?.user?.avatar ||
      worker?.user?.image_path,
  ) || WORKER_FALLBACK_AVATAR;
}

function addMinutes(date, minutes) {
  if (!(date instanceof Date) || !Number.isFinite(minutes) || minutes <= 0) {
    return null;
  }
  return new Date(date.getTime() + minutes * 60 * 1000);
}

export function TimeSlotStep({
  palette,
  lang,
  branch,
  service,
  selectedWorker,
  selectedWorkerProfession,
  user,
  isAuthenticated,
  userLoading = false,
  accountContact = {},
  selectedSlot,
  onSelectSlot,
  email,
  onEmailChange,
  clientName,
  onClientNameChange,
  phone,
  onPhoneChange,
  clientComment,
  onClientCommentChange,
  onBack,
  onNext,
}) {
  const { t } = useTranslation();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [selectedDayKey, setSelectedDayKey] = useState(() =>
    selectedSlot?.date ? dayKey(selectedSlot.date) : "",
  );
  const [requestedDayKey, setRequestedDayKey] = useState(() =>
    selectedSlot?.date ? dayKey(selectedSlot.date) : "",
  );
  const [editingTime, setEditingTime] = useState(false);
  const [calendarYear, setCalendarYear] = useState(() =>
    (selectedSlot?.date || new Date()).getFullYear(),
  );
  const [calendarMonth, setCalendarMonth] = useState(() =>
    (selectedSlot?.date || new Date()).getMonth(),
  );
  const dayScrollerRef = useRef(null);
  const dayScrollTimerRef = useRef(null);
  const dayScrollPointerRef = useRef(false);

  const branchId = branch?.id;
  // The selected service may originate from either a top-level Category
  // record (where `id` IS the category id) or a drill-down BranchCategory
  // record (where `id` is the branch_category row id and the real
  // category id is on `category_id`). Use the helper so we always send
  // the id the time-suggestions API expects.
  const primaryService = getPrimaryService(service);
  const categoryId =
    primaryService?.categoryId ?? getServiceCategoryId(primaryService);
  const categoryIds = useMemo(
    () =>
      getSelectedServices(service)
        .map((item) => item?.categoryId ?? getServiceCategoryId(item))
        .filter((id) => id != null),
    [service],
  );
  const serviceUserId = getServiceUserId(primaryService);
  const selectedWorkerId = selectedWorker?.id ?? null;
  const timeSuggestionUserId = selectedWorkerId || serviceUserId;
  const selectedServices = useMemo(() => getSelectedServices(service), [service]);
  const serviceRows = useMemo(
    () =>
      selectedServices.map((item, index) => {
        const price = getServicePrice(item);
        const minutes = getDurationMinutes(item);
        return {
          id: item?.categoryId ?? getServiceCategoryId(item) ?? item?.id ?? index,
          name: pickTranslation(item, lang),
          price,
          duration: formatDurationMinutes(minutes, lang),
          minutes,
        };
      }),
    [lang, selectedServices],
  );
  const totalServicePrice = serviceRows.reduce(
    (sum, item) => sum + Number(item.price || 0),
    0,
  );
  const hasAnyServicePrice = serviceRows.some(
    (item) => Number(item.price || 0) > 0,
  );
  const totalServiceMinutes = serviceRows.reduce(
    (sum, item) => sum + Number(item.minutes || 0),
    0,
  );
  
  const workerName = getWorkerName(selectedWorker);
  const workerRole = getWorkerRole(selectedWorker, lang, service, selectedWorkerProfession);
  const workerAvatar = getWorkerAvatar(selectedWorker);
  const selectedSlotDate = selectedSlot?.date || parseSlotDate(selectedSlot?.raw);
  const selectedDateLabel = selectedSlotDate
    ? `${String(selectedSlotDate.getDate()).padStart(2, "0")}.${String(
        selectedSlotDate.getMonth() + 1,
      ).padStart(2, "0")}.${selectedSlotDate.getFullYear()}`
    : "";
  const selectedStartTime = selectedSlotDate ? formatClockTime(selectedSlotDate) : "";
  const selectedEndDate = addMinutes(selectedSlotDate, totalServiceMinutes);
  const selectedEndTime = selectedEndDate ? formatClockTime(selectedEndDate) : "";

  const label = useCallback(
    (key, fallback) => {
      const value = t(key);
      return value === key ? fallback : value;
    },
    [t],
  );

  const load = useCallback(async () => {
    if (!branchId || !categoryId) {
      setSlots([]);
      setLoading(false);
      setError(new Error("Missing branch or service category"));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTimeSuggestions(lang, {
        branchId,
        categoryId,
        categoryIds,
        date: requestedDayKey || undefined,
        userId: timeSuggestionUserId,
      });
      const parsed = (Array.isArray(data?.data) ? data.data : [])
        .map((s) => ({ raw: s, date: parseSlotDate(s) }))
        .filter((s) => s.date != null);
      setSlots(parsed);
    } catch (err) {
      console.error("[booking] slots load failed", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [
    branchId,
    categoryId,
    categoryIds,
    lang,
    requestedDayKey,
    timeSuggestionUserId,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSelectedDayKey("");
    setRequestedDayKey("");
    setSlots([]);
    setEditingTime(false);
  }, [branchId, categoryId, timeSuggestionUserId]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const s of slots) {
      const key = dayKey(s.date);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    // Sort each day's slots, then return in date order
    return Array.from(map.entries())
      .map(([key, list]) => ({
        key,
        date: list[0].date,
        slots: [...list].sort((a, b) => a.date - b.date),
      }))
      .sort((a, b) => a.date - b.date);
  }, [slots]);

  // const dayOptions = useMemo(() => {
  //   const today = new Date();
  //   today.setHours(0, 0, 0, 0);
  //   const slotMap = new Map(grouped.map((group) => [group.key, group]));
  //   const options = [];

  //   for (let i = 0; i < 30; i += 1) {
  //     const date = new Date(today);
  //     date.setDate(today.getDate() + i);
  //     const key = dayKey(date);
  //     const existing = slotMap.get(key);
  //     options.push(existing || { key, date, slots: [] });
  //   }

  //   for (const group of grouped) {
  //     if (!options.some((option) => option.key === group.key)) {
  //       options.push(group);
  //     }
  //   }

  //   return options;
  // }, [grouped]);


  const dayOptions = useMemo(() => {
    const slotMap = new Map(grouped.map((group) => [group.key, group]));
    const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate();

    return Array.from({ length: daysInMonth }, (_, index) => {
      const date = new Date(calendarYear, calendarMonth, index + 1);
      const key = dayKey(date);
      return (
        slotMap.get(key) || {
          key,
          date,
          slots: [],
        }
      );
    });
  }, [calendarMonth, calendarYear, grouped]);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = new Set([
      currentYear,
      currentYear + 1,
      calendarYear,
      ...grouped.map((group) => group.date.getFullYear()),
    ]);
    return Array.from(years).sort((a, b) => a - b);
  }, [calendarYear, grouped]);

  const monthOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, month) => month);
  }, []);

  useEffect(() => {
    if (!grouped[0] || selectedDayKey || requestedDayKey) return;
    setCalendarYear(grouped[0].date.getFullYear());
    setCalendarMonth(grouped[0].date.getMonth());
  }, [grouped, requestedDayKey, selectedDayKey]);

  useEffect(() => {
    if (requestedDayKey) {
      if (selectedDayKey !== requestedDayKey) setSelectedDayKey(requestedDayKey);
      return;
    }
    if (selectedSlot?.date) {
      setSelectedDayKey(dayKey(selectedSlot.date));
      return;
    }
    if (!selectedDayKey && (grouped[0]?.key || dayOptions[0]?.key)) {
      setSelectedDayKey(grouped[0]?.key || dayOptions[0].key);
    }
  }, [dayOptions, grouped, loading, requestedDayKey, selectedDayKey, selectedSlot]);

  const selectedGroup = useMemo(() => {
    return dayOptions.find((group) => group.key === selectedDayKey) || dayOptions[0];
  }, [dayOptions, selectedDayKey]);

  const accountEmail = accountContact.email || user?.email || "";
  const accountPhone = accountContact.phone || user?.phone || user?.phone_number || "";
  const accountName = accountContact.name || user?.name || "";
  const effectiveClientName = clientName || accountName || "";
  const effectiveEmail = email || accountEmail || "";
  const effectivePhone = phone || accountPhone || "";
  const trimmedEmail = String(effectiveEmail).trim();
  const trimmedPhone = String(effectivePhone).trim();
  const canAskForMissingContact = !isAuthenticated || !userLoading;
  const shouldAskName = canAskForMissingContact;
  const shouldAskEmail = canAskForMissingContact;
  const shouldAskPhone = canAskForMissingContact;
  const phoneFilled = trimmedPhone.length > 0;
  const emailFilled = trimmedEmail.length > 0;
  const phoneValid = !phoneFilled || phoneIsValid(trimmedPhone);
  const showContactInputs =
    canAskForMissingContact &&
    (shouldAskName || shouldAskEmail || shouldAskPhone);
  const emailValid = !emailFilled || emailIsValid(trimmedEmail);
  const contactReady =
    canAskForMissingContact && emailValid && phoneValid;
  const canContinue =
    Boolean(selectedSlot) && contactReady && !loading && !error;

  const handleConfirm = useCallback(() => {
    if (emailFilled && !emailValid) setEmailTouched(true);
    if (phoneFilled && !phoneValid) setPhoneTouched(true);
    if (!canContinue) return;
    onNext();
  }, [canContinue, emailFilled, emailValid, onNext, phoneFilled, phoneValid]);

  const formatCalendarMonth = useCallback((month) => {
    const normalizedLang = String(lang || "hy").split("-")[0];
    return (
      CALENDAR_MONTHS[normalizedLang]?.[month] ||
      CALENDAR_MONTHS.hy[month] ||
      formatMonth(new Date(calendarYear, month, 1), t)
    );
  }, [calendarYear, lang, t]);

  const pickCalendarDate = useCallback((year, month) => {
    const matchingGroup = grouped.find(
      (group) =>
        group.date.getFullYear() === year && group.date.getMonth() === month,
    );
    const targetDate = matchingGroup?.date || new Date(year, month, 1);
    setSelectedDayKey(dayKey(targetDate));
    setRequestedDayKey("");
  }, [grouped]);

  const handleCalendarYearChange = useCallback((event) => {
    const nextYear = Number(event.target.value);
    setCalendarYear(nextYear);
    pickCalendarDate(nextYear, calendarMonth);
  }, [calendarMonth, pickCalendarDate]);

  const handleCalendarMonthChange = useCallback((event) => {
    const nextMonth = Number(event.target.value);
    setCalendarMonth(nextMonth);
    pickCalendarDate(calendarYear, nextMonth);
  }, [calendarYear, pickCalendarDate]);

  const scrollDays = useCallback((direction) => {
    dayScrollerRef.current?.scrollBy({
      left: direction * 420,
      behavior: "smooth",
    });
  }, []);

  const stopDayAutoScroll = useCallback(() => {
    if (!dayScrollTimerRef.current) return;
    window.clearInterval(dayScrollTimerRef.current);
    dayScrollTimerRef.current = null;
  }, []);

  const startDayAutoScroll = useCallback((direction) => {
    stopDayAutoScroll();
    dayScrollTimerRef.current = window.setInterval(() => {
      dayScrollerRef.current?.scrollBy({ left: direction * 16, behavior: "auto" });
    }, 20);
  }, [stopDayAutoScroll]);

  const handleDayScrollClick = useCallback((direction) => {
    stopDayAutoScroll();
    scrollDays(direction);
  }, [scrollDays, stopDayAutoScroll]);

  const handleDayScrollPointer = useCallback((direction) => {
    dayScrollPointerRef.current = true;
    handleDayScrollClick(direction);
  }, [handleDayScrollClick]);

  const handleDayScrollKeyboardClick = useCallback((direction) => {
    if (dayScrollPointerRef.current) {
      dayScrollPointerRef.current = false;
      return;
    }
    handleDayScrollClick(direction);
  }, [handleDayScrollClick]);

  useEffect(() => stopDayAutoScroll, [stopDayAutoScroll]);

  const contactColumnStyle = {
    width: "100%",
    maxWidth: 613,
    justifySelf: "start",
  };

  const contactHintStyle = {
    width: "100%",
    maxWidth: 850,
    justifySelf: "start",
  };

  const contactLabelStyle = {
    color: palette.text,
    fontSize: "14.4px",
    lineHeight: "21.6px",
  };

  const contactInputStyle = {
    width: "100%",
    background: palette.surfaceAlt,
    border: palette.border,
    borderRadius: 12,
    padding: "13px 15px 14px",
    color: palette.text,
    fontSize: "15.2px",
    lineHeight: 1.2,
    outline: "none",
  };

  const calendarSelectShellStyle = {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    flex: "0 0 auto",
  };

  const calendarSelectStyle = {
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    background: palette.accentSoft,
    border: "none",
    borderRadius: 999,
    color: palette.accent,
    padding: "8px 32px 8px 12px",
    minHeight: 32,
    fontSize: "12.8px",
    lineHeight: "12.8px",
    outline: "none",
    cursor: "pointer",
  };

  const detailsMaxWidth = 719;
  const detailsRowStyle = {
    width: "100%",
    maxWidth: detailsMaxWidth,
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "16px 0 17px",
    borderBottom: palette.border,
  };
  const iconTileStyle = {
    width: 46,
    height: 46,
    borderRadius: 14,
    flex: "0 0 auto",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(0,0,0,0.2)",
    color: palette.text,
    fontSize: 24,
  };
  const editButtonStyle = {
    width: 36,
    height: 36,
    border: "none",
    background: "transparent",
    color: palette.text,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    cursor: "pointer",
    fontSize: 24,
    flex: "0 0 auto",
  };

  return (
    <StepCard palette={palette}>
      <div
        style={{
          padding: `16px ${STEP_PAD.x} 21px ${STEP_PAD.x}`,
          borderBottom: palette.border,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <button
          type="button"
          onClick={onBack}
          className="pill fw-500"
          style={{
            background: "transparent",
            border: palette.border,
            color: palette.textMuted,
            padding: "9px 19px",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontSize: "1rem",
            lineHeight: 1.25,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            style={{ flex: "0 0 auto" }}
          >
            <path
              d="M19 12H5m0 0 6-6m-6 6 6 6"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {t("booking.common.back")}
        </button>
        {editingTime || !selectedSlot ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 10,
              flex: "1 1 260px",
            }}
          >
          <span style={calendarSelectShellStyle}>
            <select
              value={calendarYear}
              onChange={handleCalendarYearChange}
              aria-label="Select year"
              style={calendarSelectStyle}
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
              style={{
                position: "absolute",
                right: 12,
                top: "50%",
                transform: "translateY(-50%)",
                color: palette.accent,
                fontSize: 14,
                pointerEvents: "none",
              }}
            >
              <path
                d="m6 9 6 6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span style={calendarSelectShellStyle}>
            <select
              value={calendarMonth}
              onChange={handleCalendarMonthChange}
              aria-label="Select month"
              style={calendarSelectStyle}
            >
              {monthOptions.map((month) => (
                <option key={month} value={month}>
                  {formatCalendarMonth(month)}
                </option>
              ))}
            </select>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
              style={{
                position: "absolute",
                right: 12,
                top: "50%",
                transform: "translateY(-50%)",
                color: palette.accent,
                fontSize: 14,
                pointerEvents: "none",
              }}
            >
              <path
                d="m6 9 6 6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          </div>
        ) : null}
      </div>
      <div
        style={{
          padding: `10px ${STEP_PAD.x} 16px ${STEP_PAD.x}`,
          display: "grid",
          gap: 16,
          fontFamily: BOOKING_FONT_FAMILY,
        }}
      >
        <h3
          className="mb-0 fw-700"
          style={{
            color: palette.text,
            fontSize: "clamp(1.15rem, 3vw, 1.4rem)",
            lineHeight: 1.25,
          }}
        >
          {label("booking.datetime.detailsTitle", "Գրանցման մանրամասներ")}
        </h3>

        {selectedWorker ? (
          <div style={detailsRowStyle}>
            <span
              style={{
                ...iconTileStyle,
                overflow: "hidden",
                background: palette.accentSoft,
                border: `1px solid ${palette.accentBorder}`,
              }}
            >
              <img
                src={workerAvatar}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={(event) => {
                  if (event.currentTarget.src.endsWith(WORKER_FALLBACK_AVATAR)) return;
                  event.currentTarget.src = WORKER_FALLBACK_AVATAR;
                }}
              />
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <strong
                className="d-block"
                style={{
                  color: palette.text,
                  fontSize: "16.6px",
                  lineHeight: "20.8px",
                  wordBreak: "break-word",
                }}
              >
                {workerName || label("booking.specialists.fallbackName", "Մասնագետ")}
              </strong>
              {workerRole ? (
                <span
                  className="d-block"
                  style={{
                    color: palette.textMuted,
                    fontSize: "14.4px",
                    lineHeight: "20.88px",
                    marginTop: 4,
                    textTransform: "uppercase",
                  }}
                >
                  {workerRole}
                </span>
              ) : null}
            </span>
            <button
              type="button"
              onClick={onBack}
              aria-label={label("booking.common.edit", "Edit")}
              style={editButtonStyle}
            >
              <i className="las la-pen" aria-hidden="true" />
            </button>
          </div>
        ) : null}

        {selectedSlot ? (
          <div style={detailsRowStyle}>
            <span style={iconTileStyle}>
              <i className="las la-calendar" aria-hidden="true" />
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span
                className="d-block"
                style={{
                  color: palette.textMuted,
                  fontSize: "14.4px",
                  lineHeight: "20.88px",
                }}
              >
                {selectedDateLabel}
              </span>
              <strong
                className="d-block"
                style={{
                  color: palette.text,
                  fontSize: "16.6px",
                  lineHeight: "20.8px",
                  marginTop: 4,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {selectedStartTime}
                {selectedEndTime ? ` - ${selectedEndTime}` : ""}
              </strong>
            </span>
            <button
              type="button"
              onClick={() => setEditingTime((value) => !value)}
              aria-label={label("booking.common.edit", "Edit")}
              style={editButtonStyle}
            >
              <i className="las la-pen" aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </div>

      {editingTime || !selectedSlot ? (
        <>
          <div
            style={{
              padding: `10px ${STEP_PAD.x} 4px ${STEP_PAD.x}`,
              fontFamily: BOOKING_FONT_FAMILY,
            }}
          >
            <h3
              className="mb-1 fw-700"
              style={{
                color: palette.text,
                fontSize: "clamp(1.15rem, 3vw, 1.4rem)",
                lineHeight: 1.25,
              }}
            >
              {t("booking.datetime.title")}
            </h3>
            <p
              className="mb-0"
              style={{
                color: palette.textMuted,
                fontSize: "15.2px",
                lineHeight: "21.28px",
              }}
            >
              {t("booking.datetime.subtitle")}
            </p>
          </div>

          <div style={{ padding: `8px ${STEP_PAD.x} 4px ${STEP_PAD.x}` }}>
        {loading && slots.length === 0 && !requestedDayKey ? (
          <div className="d-grid gap-3">
            {[0, 1, 2].map((i) => (
              <div key={i}>
                <div
                  style={{
                    width: 120,
                    height: 14,
                    borderRadius: 6,
                    background: palette.surfaceAlt,
                    border: palette.border,
                    marginBottom: 10,
                  }}
                />
                <div className="d-flex flex-wrap gap-2">
                  {[0, 1, 2, 3, 4].map((j) => (
                    <div
                      key={j}
                      style={{
                        width: "clamp(72px, 18vw, 88px)",
                        height: 38,
                        borderRadius: 999,
                        background: palette.surfaceAlt,
                        border: palette.border,
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <EmptyState
            palette={palette}
            icon="las la-exclamation-circle"
            title={t("booking.errors.loadSlots")}
            action={<RetryButton palette={palette} onClick={load} />}
          />
        ) : grouped.length === 0 && !requestedDayKey ? (
          <EmptyState
            palette={palette}
            icon="las la-clock"
            title={t("booking.datetime.noSlots")}
          />
        ) : (
          <div className="d-grid gap-3">
            <div
              style={{
                position: "relative",
                overflow: "hidden",
                minHeight: 112,
              }}
            >
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 64,
                  zIndex: 1,
                  pointerEvents: "none",
                  background: `linear-gradient(90deg, ${palette.surface} 0%, rgba(0,0,0,0) 100%)`,
                }}
              />
              <button
                type="button"
                onPointerDown={() => handleDayScrollPointer(-1)}
                onClick={() => handleDayScrollKeyboardClick(-1)}
                onMouseEnter={() => startDayAutoScroll(-1)}
                onMouseLeave={stopDayAutoScroll}
                onFocus={() => startDayAutoScroll(-1)}
                onBlur={stopDayAutoScroll}
                aria-label="Scroll days left"
                style={{
                  position: "absolute",
                  left: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  width: 38,
                  height: 38,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  border: palette.border,
                  background: palette.surface,
                  color: palette.accent,
                  boxShadow: "0 12px 26px rgba(0,0,0,0.18)",
                  cursor: "pointer",
                }}
              >
                <i className="las la-angle-left" aria-hidden="true" />
              </button>
              <div
                ref={dayScrollerRef}
                className="booking-day-strip"
                style={{
                  display: "flex",
                  gap: 10,
                  overflowX: "auto",
                  overflowY: "hidden",
                  scrollBehavior: "smooth",
                  padding: "2px 58px 10px",
                  minHeight: 110,
                  alignItems: "stretch",
                }}
              >
                {dayOptions.map((group) => {
                  const isSelected = group.key === selectedGroup?.key;
                  const hasSlots = group.slots.length > 0;
                  return (
                    <button
                      key={group.key}
                      type="button"
                      onClick={() => {
                        setSelectedDayKey(group.key);
                        setRequestedDayKey(group.key);
                      }}
                      className="fw-700"
                      style={{
                        minWidth: 86,
                        height: 76,
                        boxSizing: "border-box",
                        borderRadius: 12,
                        padding: "8px 10px",
                        border: `2px solid ${isSelected ? palette.accent : "rgba(255,255,255,0.08)"
                          }`,
                        background: isSelected
                          ? palette.surfaceAlt
                          : palette.surfaceAlt,
                        color: palette.text,
                        cursor: "pointer",
                        opacity: hasSlots || isSelected ? 1 : 0.72,
                        textAlign: "center",
                        boxShadow: isSelected
                          ? "0 12px 26px rgba(0,0,0,0.18)"
                          : "none",
                        transition: "background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease, opacity 0.16s ease, box-shadow 0.16s ease",
                        flex: "0 0 auto",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      aria-pressed={isSelected}
                    >
                      <span
                        className="d-block"
                        style={{
                          fontSize: "0.78rem",
                          color: palette.textMuted,
                          textTransform: "uppercase",
                        }}
                      >

                        {formatWeekday(group.date, t)}
                      </span>
                      <span
                        className="d-block"
                        style={{
                          fontSize: "1.55rem",
                          lineHeight: 1,
                          marginTop: 2,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {group.date.getDate()}
                      </span>
                      <span
                        className="d-block fw-500"
                        style={{
                          fontSize: "0.78rem",
                          color: palette.textMuted,
                          marginTop: 2,
                        }}
                      >
                        {formatMonth(group.date, t)}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: 76,
                  zIndex: 1,
                  pointerEvents: "none",
                  background: `linear-gradient(270deg, ${palette.surface} 0%, rgba(0,0,0,0) 100%)`,
                }}
              />
              <button
                type="button"
                onPointerDown={() => handleDayScrollPointer(1)}
                onClick={() => handleDayScrollKeyboardClick(1)}
                onMouseEnter={() => startDayAutoScroll(1)}
                onMouseLeave={stopDayAutoScroll}
                onFocus={() => startDayAutoScroll(1)}
                onBlur={stopDayAutoScroll}
                aria-label="Scroll days right"
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  width: 38,
                  height: 38,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  border: palette.border,
                  background: palette.surface,
                  color: palette.accent,
                  boxShadow: "0 12px 26px rgba(0,0,0,0.18)",
                  cursor: "pointer",
                }}
              >
                <i className="las la-angle-right" aria-hidden="true" />
              </button>
              <style>
                {`.booking-day-strip{scrollbar-width:none;-ms-overflow-style:none}.booking-day-strip::-webkit-scrollbar{display:none}.booking-day-strip button{font-family:"Segoe UI","Noto Sans Armenian",Arial,sans-serif}`}
              </style>
            </div>

            {selectedGroup ? (
              <div style={{ minHeight: 128 }}>
                <div
                  className="fw-700 mb-2"
                  style={{ color: palette.text, fontSize: "1rem" }}
                >
                  {formatDayLabel(selectedGroup.date, lang, t, { relative: true })}
                </div>
                {loading && requestedDayKey ? (
                  <div
                    style={{
                      background: palette.surfaceAlt,
                      border: palette.border,
                      borderRadius: 14,
                      padding: "14px 16px",
                      minHeight: 52,
                      color: palette.textMuted,
                      fontSize: "0.92rem",
                    }}
                  >
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      style={{ color: palette.accent }}
                      aria-hidden="true"
                    />
                    {t("booking.common.loading")}
                  </div>
                ) : selectedGroup.slots.length === 0 && !loading ? (
                  <div
                    style={{
                      background: palette.surfaceAlt,
                      border: palette.border,
                      borderRadius: 14,
                      padding: "14px 16px",
                      minHeight: 52,
                      color: palette.textMuted,
                      fontSize: "0.92rem",
                    }}
                  >
                    <i className="las la-clock me-1" aria-hidden="true" />
                    {t("booking.datetime.noDaySlotsFull")}
                  </div>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(82px, 1fr))",
                      gap: 10,
                    }}
                  >
                    {selectedGroup.slots.map((slot) => {
                      const isSelected =
                        selectedSlot && selectedSlot.raw === slot.raw;
                      return (
                        <button
                          key={slot.raw}
                          type="button"
                          onClick={() => {
                            onSelectSlot(slot);
                            setEditingTime(false);
                          }}
                          className="fw-700"
                          style={{
                            padding: "8px 16px",
                            borderRadius: 999,
                            background: isSelected
                              ? palette.accent
                              : palette.surfaceAlt,
                            color: isSelected ? "#061415" : palette.text,
                            border: isSelected
                              ? `2px solid ${palette.accent}`
                              : palette.border,
                            cursor: "pointer",
                            fontSize: "0.95rem",
                            fontVariantNumeric: "tabular-nums",
                            transition: "all 0.15s ease",
                          }}
                        >
                          {formatClockTime(slot.date)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>
        </>
      ) : null}

      {serviceRows.length > 0 ? (
        <div
          style={{
            padding: `12px ${STEP_PAD.x} 24px ${STEP_PAD.x}`,
            display: "grid",
            gap: 16,
            fontFamily: BOOKING_FONT_FAMILY,
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: detailsMaxWidth,
              display: "flex",
              alignItems: "center",
              gap: 16,
            }}
          >
            <h3
              className="mb-0 fw-700"
              style={{
                color: palette.text,
                fontSize: 20,
                lineHeight: "20.8px",
              }}
            >
              {label("booking.datetime.servicesTitle", "Ծառայություններ")}
            </h3>
            {totalServiceMinutes > 0 ? (
              <span
                style={{
                  color: palette.textMuted,
                  fontSize: 14,
                  lineHeight: "20.88px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <i className="las la-clock" aria-hidden="true" />
                {formatDurationMinutes(totalServiceMinutes, lang)}
              </span>
            ) : null}
          </div>
          <div style={{ width: "100%", maxWidth: detailsMaxWidth }}>
            {serviceRows.map((row, index) => (
              <div
                key={row.id}
                style={{
                  padding: index === serviceRows.length - 1 ? "8px 0 12px" : "8px 0",
                  borderBottom:
                    index === serviceRows.length - 1 ? "none" : palette.border,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 16,
                  }}
                >
                  <span
                    style={{
                      color: palette.text,
                      fontSize: "16.6px",
                      lineHeight: "20.8px",
                      minWidth: 0,
                      wordBreak: "break-word",
                    }}
                  >
                    {row.name}
                  </span>
                  {Number(row.price || 0) > 0 ? (
                    <strong
                      style={{
                        color: palette.text,
                        fontSize: 20,
                        lineHeight: "30.4px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatServicePrice(row.price)}
                    </strong>
                  ) : null}
                </div>
                {row.duration ? (
                  <div
                    style={{
                      color: palette.textMuted,
                      fontSize: 14,
                      lineHeight: "20.88px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <i className="las la-clock" aria-hidden="true" />
                    {row.duration}
                  </div>
                ) : null}
              </div>
            ))}
            {hasAnyServicePrice ? (
              <div
                style={{
                  padding: "16px 0 0",
                  borderTop: palette.border,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 16,
                }}
              >
                <span
                  style={{
                    color: palette.text,
                    fontSize: "16.6px",
                    lineHeight: "20.8px",
                  }}
                >
                  {label("booking.datetime.total", "Ընդհանուր")}
                </span>
                <span
                  style={{
                    color: palette.text,
                    fontSize: 20,
                    lineHeight: "30.4px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatServicePrice(totalServicePrice)}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Contact / identity inputs */}
      <div
        style={{
          padding: `28px ${STEP_PAD.x} 20px ${STEP_PAD.x}`,
          borderTop: palette.border,
          marginTop: 18,
          display: "grid",
          gap: 14,
        }}
      >
        {showContactInputs ? (
          <>
            {/* Anonymous user — gather contact details so we can email/SMS
                them once the backend wires up notifications. */}
            <div
              style={{
                ...contactHintStyle,
                fontSize: "0.85rem",
                color: palette.textMuted,
                lineHeight: 1.45,
                display: "flex",
                alignItems: "flex-start",
                gap: 6,
              }}
            >

              <span>
                {t("booking.datetime.contactHint")}
              </span>
            </div>

            {shouldAskName ? (
              <div style={contactColumnStyle}>
                <label
                  className="d-block fw-600 mb-1"
                  style={contactLabelStyle}
                >
                  {t("booking.datetime.nameLabel")}
                </label>
                <input
                  type="text"
                  value={effectiveClientName}
                  onChange={(e) => onClientNameChange(e.target.value)}
                  placeholder={t("booking.datetime.namePlaceholder")}
                  style={{
                    ...contactInputStyle,
                  }}
                />
              </div>
            ) : null}

            {shouldAskPhone ? (
              <div style={contactColumnStyle}>
                <label
                  className="d-block fw-600 mb-1"
                  style={contactLabelStyle}
                >
                  {t("booking.datetime.phoneLabel")}
                </label>
                <input
                  type="tel"
                  value={effectivePhone}
                  onChange={(e) => onPhoneChange(e.target.value)}
                  onBlur={() => setPhoneTouched(true)}
                  placeholder={t("booking.datetime.phonePlaceholder")}
                  style={{
                    ...contactInputStyle,
                    border:
                      phoneTouched && phoneFilled && !phoneValid
                        ? "2px solid hsl(0 80% 55%)"
                        : palette.border,
                  }}
                />
                {phoneTouched && phoneFilled && !phoneValid ? (
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: "0.78rem",
                      color: "hsl(0 80% 55%)",
                    }}
                  >
                    {t("booking.datetime.phoneInvalid")}
                  </div>
                ) : null}
              </div>
            ) : null}

            {shouldAskEmail ? (
              <div style={contactColumnStyle}>
                <label
                  className="d-block fw-600 mb-1"
                  style={contactLabelStyle}
                >
                  {t("booking.datetime.emailLabel")}
                </label>
                <input
                  type="email"
                  value={effectiveEmail}
                  onChange={(e) => onEmailChange(e.target.value)}
                  onBlur={() => setEmailTouched(true)}
                  placeholder={t("booking.datetime.emailPlaceholder")}
                  style={{
                    ...contactInputStyle,
                    border:
                      emailTouched && emailFilled && !emailValid
                        ? "2px solid hsl(0 80% 55%)"
                        : palette.border,
                  }}
                />
                {emailTouched && emailFilled && !emailValid ? (
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: "0.78rem",
                      color: "hsl(0 80% 55%)",
                    }}
                  >
                    {t("booking.datetime.emailInvalid")}
                  </div>
                ) : (
                  <div
                    style={{
                      marginTop: 10,
                      background: palette.accentSoft,
                      border: `1px solid ${palette.accentBorder}`,
                      borderRadius: 14,
                      padding: "13px 15px",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      color: palette.text,
                      minHeight: 40,
                    }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                      style={{ color: palette.accent, flex: "0 0 auto" }}
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="9"
                        stroke="currentColor"
                        strokeWidth="1.8"
                      />
                      <path
                        d="M12 11v5"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      />
                      <path
                        d="M12 8h.01"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                      />
                    </svg>
                    <span
                      style={{
                        color: palette.text,
                        fontSize: "0.95rem",
                        fontWeight: 700,
                        lineHeight: 1.5,

                      }}
                    >
                      {t("booking.datetime.emailHint")}
                    </span>
                  </div>
                )}
              </div>
            ) : null}
          </>
        ) : isAuthenticated && userLoading ? (
          <div
            className="d-flex align-items-center gap-2"
            style={{
              ...contactColumnStyle,
              background: palette.accentSoft,
              color: palette.text,
              border: palette.border,
              borderRadius: 12,
              padding: "10px 14px",
              fontSize: "0.9rem",
            }}
          >
            <i
              className="las la-user-clock"
              style={{ color: palette.accent, fontSize: "1.2rem" }}
              aria-hidden="true"
            />
            <span>{t("booking.datetime.accountLoading")}</span>
          </div>
        ) : accountEmail || accountPhone ? (
          <div
            className="d-flex align-items-center gap-2"
            style={{
              ...contactColumnStyle,
              background: palette.accentSoft,
              color: palette.text,
              border: palette.border,
              borderRadius: 12,
              padding: "10px 14px",
              fontSize: "0.9rem",
            }}
          >
            <i
              className="las la-user-check"
              style={{ color: palette.accent, fontSize: "1.2rem" }}
              aria-hidden="true"
            />
            <span>
              {t("booking.datetime.loggedInHint", {
                email: accountEmail || accountPhone,
              })}
            </span>
          </div>
        ) : null}

        <div style={contactColumnStyle}>
          <label
            className="d-block fw-600 mb-1"
            style={contactLabelStyle}
          >
            {t("booking.datetime.commentLabel")}
          </label>
          <textarea
            value={clientComment || ""}
            onChange={(e) => onClientCommentChange(e.target.value)}
            placeholder={t("booking.datetime.commentPlaceholder")}
            rows={3}
            style={{
              ...contactInputStyle,
              minHeight: 92,
              resize: "vertical",
              lineHeight: 1.5,
            }}
          />
        </div>
      </div>

      <div
        style={{
          padding: `17px ${STEP_PAD.x} 20px ${STEP_PAD.x}`,
          borderTop: palette.border,
          display: "flex",
          justifyContent: "flex-start",
        }}
      >
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!canContinue}
          className="pill fw-500"
          style={{
            background: canContinue ? palette.accent : palette.surfaceAlt,
            color: canContinue ? "#061415" : palette.textMuted,
            border: canContinue ? "none" : palette.border,
            padding: "10px 24px",
            minWidth: 302,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            opacity: canContinue ? 1 : 0.5,
            cursor: canContinue ? "pointer" : "not-allowed",
          }}
        >
          {t("booking.common.confirm")}
          <i className="las la-check" aria-hidden="true" />
        </button>
      </div>
    </StepCard>
  );
}
